/**
 * API response types. Everything that is part of a stable contract lives in
 * `@ismart/shared` (imported below); this file is where page-specific payloads
 * accumulate until they are worth promoting to the shared package.
 */

import type { AuthMe, HealthResponse } from '@ismart/shared';

export type { AuthMe, HealthResponse };

/** Machine status of a job/target (SPEC §6a vocabulary). */
export type JobStatusValue =
  | 'unknown'
  | 'healthy'
  | 'warning'
  | 'failing'
  | 'stale'
  | 'frozen'
  | 'suspended'
  | 'recorded';

export type RunStatusValue = 'running' | 'ok' | 'incomplete' | 'failed';

/** Execution plane of a job (board grouping label, mirrors the API's Zone). */
export type Zone = 'ru' | 'kz-dr' | 'kz-vm' | 'gh' | 'laptop';

export const ZONES: readonly Zone[] = ['ru', 'kz-dr', 'kz-vm', 'gh', 'laptop'];

/**
 * Whose estate a job/target protects (the board's PRIMARY grouping, K3; K4
 * remap: the project belongs to the TARGET — a job spanning projects renders in
 * each section). Open enum-ish string on the API; the board pins these in order
 * and files anything else under the `other` section.
 */
export type Project = string;

/**
 * Pinned board order (K4, CTO remap 2026-10-12 — must mirror the API's
 * PINNED_PROJECTS): products first, then the service sections.
 */
export const PROJECT_ORDER: readonly Project[] = [
  'ismart',
  'kpk',
  'code-of-future',
  'cb-agreements',
  'cb-manager',
  'partner-cabinet',
  'llm-router',
  'online-school',
  'cloudflare',
  'infra',
  'monitor',
];

/** Human label per pinned project (the board's section headers). */
export const PROJECT_META: Record<string, { title: string }> = {
  ismart: { title: 'Платформа iSmart' },
  kpk: { title: 'КПК' },
  'code-of-future': { title: 'Код будущего' },
  'cb-agreements': { title: 'CB Agreements' },
  'cb-manager': { title: 'CB Manager' },
  'partner-cabinet': { title: 'Кабинет партнёра' },
  'llm-router': { title: 'LLM Router' },
  'online-school': { title: 'Онлайн-школа' },
  cloudflare: { title: 'Cloudflare (DNS)' },
  infra: { title: 'Инфраструктура' },
  monitor: { title: 'Мониторинг/верификация' },
};

/** The catch-all section name for projects outside the pinned order. */
export const OTHER_PROJECT = 'other';

/** S3 target the freshness sweep watches (bucket + RU/KZ endpoint). */
export interface SweepRef {
  enabled: boolean;
  bucket: string | null;
  endpoint: 'ru' | 'kz';
  prefix: string;
}

export interface JobStatusView {
  status: JobStatusValue;
  reason: string;
  age_s: number | null;
}

export interface TargetView {
  name: string;
  status: JobStatusValue;
  age_s: number | null;
  size_bytes: number | null;
  path: string | null;
  sha256: string | null;
  kz: 'ok' | 'fail' | 'skip' | null;
  last_run_ts: string | null;
  last_run_status: string | null;
  /** Whose estate this target protects (K4: its own, else the job's). */
  project: Project;
}

export interface RunMech {
  cluster: string;
  ns: string;
  cronjob: string;
}

export interface RunSummaryView {
  run: string;
  status: RunStatusValue;
  started: string | null;
  ended: string | null;
  duration_s: number | null;
  expected_count: number;
  uploaded: number | null;
  skipped: number | null;
  failed: number | null;
  deleted: number | null;
  headless: boolean;
}

export interface RunCapability {
  enabled: boolean;
  supported: boolean;
  reason: string | null;
}

export interface JobView {
  id: string;
  name: string;
  where: string;
  zone: Zone;
  project: Project;
  /** Distinct projects of its targets (K4), pinned order first. */
  projects: Project[];
  suspended: boolean;
  suspendedReason: string | null;
  cron: string | null;
  timezone: string;
  engine: string;
  btype: string;
  kind: 'job' | 'mirror';
  rpoHours: number;
  instrumented: boolean;
  runmech: RunMech | null;
  /** Job-level retention default (a target's own `keep` wins over it). */
  keep: number | null;
  /** The S3 target the freshness sweep watches. */
  sweep: SweepRef;
  status: JobStatusView;
  targets: TargetView[];
  planned: string[];
  alertsMuted: boolean;
}

export interface JobDetailView extends JobView {
  targets_spec: Array<{ name: string; prefix: string; artifact: 'file' | 'datedir'; sidecar: boolean; keep?: number | null; dbs?: string[] }>;
  runs: RunSummaryView[];
  run: RunCapability;
}

export interface TargetMarkView {
  status: string;
  ts: string;
  size_bytes: number | null;
  path: string | null;
  sha256: string | null;
  kz: string | null;
}

export interface RunDetailView {
  job: string;
  run: string;
  status: RunStatusValue;
  started: string | null;
  ended: string | null;
  duration_s: number | null;
  prev: { run: string; duration_s: number | null } | null;
  counters: { uploaded: number; skipped: number; failed: number; deleted: number } | null;
  expected: string[];
  registry_expected: string[] | null;
  missing: string[];
  drift: boolean;
  headless: boolean;
  targets: Record<string, TargetMarkView>;
  warnings: string[];
  events: Array<Record<string, unknown>>;
}

export interface SyncBucketState {
  bucket: string;
  list?: 'nightly' | 'midday';
  mirrorJob?: string;
  state: 'converged' | 'in_flight' | 'overdue' | 'diverged' | 'unknown';
  kzOnly?: boolean;
  kzOnlySample?: string[];
  ruCount: number | null;
  kzCount: number | null;
  ruSizeBytes: number | null;
  kzSizeBytes: number | null;
  ruNewestAgeHours: number | null;
  kzLagHours: number | null;
  kzEmpty?: boolean;
  truncated?: boolean;
  countDiff: number | null;
  sizeDiffBytes: number | null;
  inFlightRun?: string | null;
  reason?: string;
  error?: string;
}

/** Metrics P1 (W4) — the overview strip and the /metrics page share these. */
export interface SuccessRate {
  total: number;
  ok: number;
  pct: number | null;
}

export interface BucketUsage {
  bucket: string;
  ruBytes: number | null;
  kzBytes: number | null;
  ruQuotaBytes: number | null;
  kzQuotaBytes: number | null;
  ruPct: number | null;
  kzPct: number | null;
  partial: boolean;
  nearLimit: boolean;
}

export interface VerifyDay {
  day: string;
  runs: number;
  ok: number;
  fail: number;
  lastNote: string | null;
}

export interface OverviewMetrics {
  success_rate_7d: SuccessRate;
  rpo_compliance: { total: number; within: number; pct: number | null };
  bucket_usage: BucketUsage[];
  restore_verify: { job: string; board: VerifyDay[]; missed_days: number };
}

export interface DurationTrend {
  lastS: number | null;
  medianS: number | null;
  samples: number;
  anomalous: boolean;
}

export interface SizeTrend {
  target: string;
  lastBytes: number | null;
  medianBytes: number | null;
  samples: number;
  anomalous: boolean;
  zero: boolean;
}

export interface JobMetrics {
  job: string;
  successRate7d: SuccessRate;
  duration: DurationTrend;
  sizeAnomalies: SizeTrend[];
  sizeZero: string[];
}

export interface UsageSample {
  day: number;
  ts: number;
  ruBytes: number | null;
  kzBytes: number | null;
}

export interface MetricsView {
  generated_at: string;
  window_days: number;
  jobs: JobMetrics[];
  success_rate_7d: SuccessRate;
  rpo_compliance: { total: number; within: number; pct: number | null; violators: Array<{ job: string; target: string; ageHours: number; rpoHours: number }> };
  mirror_lag: Array<{ bucket: string; kzLagHours: number | null; kzEmpty: boolean; state: string }>;
  bucket_usage: BucketUsage[];
  restore_verify: { job: string; board: VerifyDay[]; missedDays: number };
  warnings: string[];
}

export interface OverviewJob {
  id: string;
  name: string;
  where: string;
  zone: Zone;
  project: Project;
  /** Distinct projects of its targets (K4): the row renders in each section. */
  projects: Project[];
  /** Per-project target names of a spanning job (the section's slice on the board). */
  projectTargets: Record<string, string[]>;
  suspended: boolean;
  suspendedReason: string | null;
  cron: string | null;
  engine: string;
  btype: string;
  kind: 'job' | 'mirror';
  rpoHours: number;
  instrumented: boolean;
  runmech: RunMech | null;
  alertsMuted: boolean;
  status: JobStatusView;
  fresh_age_s: number | null;
  last_run: RunSummaryView | null;
}

/**
 * `disabled` is sync-only (K4): the RU→KZ comparison sweep is switched off at
 * the seed — nothing to judge, never drives `overall`.
 */
export type OverallValue = 'ok' | 'degraded' | 'critical' | 'unknown' | 'disabled';

export interface RunnerSnapshot {
  last_poll_age_s: number | null;
  queue_depth: number;
  inflight: number;
  offline: boolean;
}

export interface OverviewView {
  overall: OverallValue;
  generated_at: string;
  jobs: OverviewJob[];
  sync: {
    state: OverallValue;
    lag_hours: number | null;
    overdue: string[];
    kz_only: string[];
    ts: number | null;
    buckets: SyncBucketState[];
  };
  alerts: string[];
  runner: RunnerSnapshot;
  sweeps: { fresh_ts: number | null; sync_ts: number | null };
  metrics: OverviewMetrics;
}

export interface AlertHistoryEntry {
  key: string;
  job: string;
  severity: string;
  message: string;
  ts: number;
  resolved: boolean;
}

export interface AlertsView {
  active: string[];
  history: AlertHistoryEntry[];
}

export interface AuditEntry {
  ts: number;
  actor: string;
  action: string;
  detail: Record<string, unknown>;
}

export interface AuditView {
  items: AuditEntry[];
}

export interface AdminsView {
  admins: string[];
  you: string;
}

export interface CommandStatusView {
  commandId: string;
  jobId: string;
  state: string;
  requestedAt: string;
  detail: string | null;
}

export interface RunNowView {
  commandId: string;
  state: string;
  requestedAt: string;
  warnings: string[];
}

/** The rich `/api/health` document (SPEC §6a) — the /health page renders it. */
export interface HealthView {
  overall: OverallValue;
  generated_at: string;
  jobs: Record<
    string,
    {
      status: JobStatusValue;
      age_s: number | null;
      reason: string;
      alertsMuted: boolean;
      zone: Zone;
      project?: Project;
      /** Distinct projects of its targets (K4 remap), additive. */
      projects?: Project[];
      suspended?: boolean;
    }
  >;
  sync: { state: OverallValue; lag_hours: number | null; overdue: string[]; kz_only: string[]; ts: number | null };
  alerts: string[];
  alertsMuted: number;
  runner: RunnerSnapshot;
}
